type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type Reader = { resolve: (response: Response) => void; reject: (error: unknown) => void; removeAbort: () => void };
type Flight = { controller: AbortController; readers: Set<Reader>; key: string; epoch: number };

// Active reads only: completed responses are never retained (API is no-store).
// Stock, search, permissions, auth and fiscal readiness are deliberately absent.
const sharedPaths = new Set([
  "/api/v1/sales/warehouses", "/api/v1/users/assignable",
  "/api/v1/sales/order-registry", "/api/v1/sales/orders",
  "/api/v1/execution-requests",
]);

export function createApiReadCoordinator(fetcher: Fetcher, origin: string) {
  const flights = new Map<string, Flight>();
  let scope: string | null = null;
  let epoch = 0;
  const abortError = () => new DOMException("Чтение отменено", "AbortError");

  function invalidate() {
    epoch += 1;
    for (const flight of flights.values()) {
      flight.controller.abort();
      for (const reader of flight.readers) {
        reader.removeAbort();
        reader.reject(abortError());
      }
      flight.readers.clear();
    }
    flights.clear();
  }

  function keyFor(input: RequestInfo | URL, init?: RequestInit): string | null {
    if (!scope || input instanceof Request || (init?.method ?? "GET").toUpperCase() !== "GET") return null;
    const url = new URL(String(input), origin);
    if (url.origin !== origin || !sharedPaths.has(url.pathname)) return null;
    // No ambiguous custom transport/security semantics may be merged.
    if (init?.body || init?.integrity || init?.keepalive || init?.credentials !== "include"
      || init?.mode || init?.redirect || init?.referrer || init?.referrerPolicy) return null;
    const headers = new Headers(init.headers);
    for (const name of headers.keys()) {
      if (name !== "accept" && name !== "x-demo-role") return null;
    }
    return JSON.stringify([epoch, scope, url.href, init.cache ?? "default", [...headers.entries()].sort()]);
  }

  function read(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (init?.signal?.aborted) return Promise.reject(abortError());
    let key: string | null;
    try { key = keyFor(input, init); } catch { key = null; }
    if (!key) return fetcher(input, init);
    let flight = flights.get(key);
    if (flight && flight.readers.size >= 16) return fetcher(input, init);
    if (!flight) {
      if (flights.size >= 64) return fetcher(input, init);
      flight = { controller: new AbortController(), readers: new Set(), key, epoch };
      flights.set(key, flight);
      const pending = flight;
      // StrictMode cleanup/remount can join before starting one transport read.
      void Promise.resolve().then(async () => {
        if (pending.epoch !== epoch || pending.readers.size === 0) {
          if (flights.get(pending.key) === pending) flights.delete(pending.key);
          return;
        }
        try {
          const response = await fetcher(input, { ...init, signal: pending.controller.signal });
          if (flights.get(pending.key) === pending) flights.delete(pending.key);
          for (const reader of pending.readers) {
            reader.removeAbort();
            if (pending.epoch !== epoch) reader.reject(abortError());
            else { try { reader.resolve(response.clone()); } catch (error) { reader.reject(error); } }
          }
        } catch (error) {
          if (flights.get(pending.key) === pending) flights.delete(pending.key);
          for (const reader of pending.readers) { reader.removeAbort(); reader.reject(error); }
        } finally { pending.readers.clear(); }
      });
    }
    const pending = flight;
    return new Promise<Response>((resolve, reject) => {
      const signal = init?.signal;
      const reader: Reader = { resolve, reject, removeAbort: () => signal?.removeEventListener("abort", cancel) };
      function cancel() {
        reader.removeAbort();
        pending.readers.delete(reader);
        reject(abortError());
        queueMicrotask(() => {
          if (pending.readers.size === 0 && flights.get(pending.key) === pending) {
            flights.delete(pending.key);
            pending.controller.abort();
          }
        });
      }
      pending.readers.add(reader);
      signal?.addEventListener("abort", cancel, { once: true });
      if (signal?.aborted) cancel();
    });
  }

  return { read, invalidate, setScope(value: string | null) { invalidate(); scope = value; } };
}
